<ng-container *ngIf="canUseAdminTools">
  <div
    class="admin-tools"
    [class.is-open]="adminActionMenuService.isOpen()"
    [class.is-available]="hasVisibleActions()"
    [class.admin-tools--modal-context]="context?.kind === 'modal'"
    [attr.aria-hidden]="!hasVisibleActions()"
  >
    <div
      id="admin-panel"
      class="admin-panel"
      [class.is-open]="adminActionMenuService.isOpen()"
      [attr.aria-hidden]="!adminActionMenuService.isOpen()"
      [style.--admin-mobile-sheet-height]="mobileSheetHeightStyle"
      (click)="handlePanelClick($event)"
    >
      <button
        type="button"
        class="admin-panel-drag-handle"
        (pointerdown)="startMobileResize($event)"
        aria-label="Resize admin tools"
      >
        <span class="admin-panel-drag-handle-bar"></span>
      </button>

      <div class="admin-panel-heading">
        <div class="admin-panel-heading-main">
          <div class="admin-panel-topline">
            <div class="admin-panel-kicker">
              <fa-icon [icon]="faSparkles"></fa-icon>
              <span>Admin tools</span>
            </div>

            <div class="admin-panel-pill-group">
              <div class="admin-panel-count">
                {{ actionCount() }} {{ actionCount() === 1 ? 'action' : 'actions' }}
              </div>
              <div class="admin-panel-scope">
                {{ scopeLabel }}
              </div>
            </div>
          </div>

          <div class="admin-panel-heading-copy">
            <strong>{{ panelTitle }}</strong>
            <div class="admin-panel-subject" *ngIf="panelSubject">
              {{ panelSubject }}
            </div>
            <div class="admin-panel-subline" *ngIf="context?.kind === 'modal' && context?.meta">
              <div class="admin-panel-meta">
                {{ context?.meta }}
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          class="admin-panel-close"
          (click)="close()"
          aria-label="Close admin tools"
        >
          <fa-icon [icon]="faXmark"></fa-icon>
        </button>
      </div>

      <div class="admin-panel-body">
        <div class="admin-panel-summary" *ngIf="context?.summary as summary">
          <div class="admin-panel-summary-head" *ngIf="summary.title || summary.description">
            <div class="admin-panel-summary-title" *ngIf="summary.title">{{ summary.title }}</div>
            <div class="admin-panel-summary-description" *ngIf="summary.description">{{ summary.description }}</div>
          </div>

          <div class="admin-panel-summary-state" *ngIf="summary.loading">
            <fa-icon [icon]="faSpinnerThird" animation="spin"></fa-icon>
            <span>Loading access snapshot...</span>
          </div>

          <div class="admin-panel-summary-state is-error" *ngIf="!summary.loading && summary.error">
            <fa-icon [icon]="faTriangleExclamation"></fa-icon>
            <span>{{ summary.error }}</span>
          </div>

          <ng-container *ngIf="!summary.loading && !summary.error">
            <div class="admin-panel-summary-banner" *ngIf="summary.banner">
              {{ summary.banner }}
            </div>

            <div class="admin-panel-summary-facts" *ngIf="summary.facts?.length">
              <div class="admin-panel-summary-fact" *ngFor="let fact of summary.facts">
                <div class="admin-panel-summary-fact-copy">
                  <span class="admin-panel-summary-fact-label">{{ fact.label }}</span>
                  <span class="admin-panel-summary-fact-value">{{ fact.value }}</span>
                </div>

                <button
                  *ngIf="fact.action"
                  type="button"
                  class="admin-panel-summary-fact-action"
                  [class.is-danger]="fact.action.tone === 'danger'"
                  [disabled]="isActionDisabled(fact.action)"
                  [attr.aria-disabled]="isActionDisabled(fact.action)"
                  (click)="runAction(fact.action)"
                >
                  {{ fact.action.label }}
                </button>
              </div>
            </div>

            <div class="admin-panel-summary-stats" *ngIf="summary.stats?.length">
              <article class="admin-panel-summary-stat" *ngFor="let stat of summary.stats">
                <span class="admin-panel-summary-stat-icon" *ngIf="stat.icon">
                  <fa-icon [icon]="stat.icon"></fa-icon>
                </span>
                <span class="admin-panel-summary-stat-copy">
                  <span class="admin-panel-summary-stat-value">{{ stat.value }}</span>
                  <span class="admin-panel-summary-stat-label">{{ stat.label }}</span>
                  <span class="admin-panel-summary-stat-helper" *ngIf="stat.helper">{{ stat.helper }}</span>
                </span>
              </article>
            </div>

            <div class="admin-panel-summary-groups" *ngIf="summary.groupsTitle || summary.groups?.length || summary.emptyState">
              <div class="admin-panel-summary-groups-title" *ngIf="summary.groupsTitle">{{ summary.groupsTitle }}</div>

              <div class="admin-panel-summary-group-list" *ngIf="summary.groups?.length; else summaryEmptyState">
                <article class="admin-panel-summary-group" *ngFor="let group of summary.groups">
                  <div class="admin-panel-summary-group-topline">
                    <span class="admin-panel-summary-group-name">{{ group.name }}</span>
                    <span class="admin-panel-summary-group-badge" *ngIf="group.badge">{{ group.badge }}</span>
                  </div>
                  <div class="admin-panel-summary-group-description" *ngIf="group.description">{{ group.description }}</div>
                  <div class="admin-panel-summary-group-meta" *ngIf="group.meta">{{ group.meta }}</div>
                </article>
              </div>

              <ng-template #summaryEmptyState>
                <div class="admin-panel-summary-empty" *ngIf="summary.emptyState">
                  {{ summary.emptyState }}
                </div>
              </ng-template>
            </div>
          </ng-container>
        </div>

        <div class="admin-panel-sections">
          <div class="admin-section" *ngFor="let section of visibleSections()">
            <div class="admin-section-head" *ngIf="section.title || section.actions.length > 1">
              <div class="admin-section-title" *ngIf="section.title">
                {{ section.title }}
              </div>
              <div class="admin-section-count" *ngIf="section.actions.length > 1">
                {{ section.actions.length }}
              </div>
            </div>

            <button
              type="button"
              class="admin-action-button"
              *ngFor="let action of section.actions"
              [class.admin-action-button-primary]="action.tone === 'primary'"
              [class.admin-action-button-danger]="action.tone === 'danger'"
              [class.admin-action-button-disabled]="isActionDisabled(action)"
              [disabled]="isActionDisabled(action)"
              [attr.aria-disabled]="isActionDisabled(action)"
              (click)="runAction(action)"
            >
              <span class="admin-action-button-leading">
                <span class="admin-action-button-icon-wrap" *ngIf="action.icon">
                  <fa-icon [icon]="action.icon" class="admin-action-button-icon"></fa-icon>
                </span>

                <span class="admin-action-button-copy">
                  <span class="admin-action-button-label">
                    <span>{{ action.label }}</span>
                  </span>
                  <span class="admin-action-button-description" *ngIf="action.description">{{ action.description }}</span>
                </span>
              </span>

              <span class="admin-action-button-meta">
                <span class="admin-action-badge" *ngIf="action.badge">{{ action.badge }}</span>
                <fa-icon [icon]="faChevronRight"></fa-icon>
              </span>
            </button>
          </div>
        </div>
      </div>

      <div class="admin-panel-footer">
        <div class="admin-panel-version">
          <div class="admin-panel-version-copy">
            <div class="admin-panel-version-topline">
              <span class="admin-panel-version-label">Running build</span>
              <span
                class="admin-panel-version-env"
                *ngIf="buildEnvironment"
                [class.is-production]="isProductionBuild"
              >{{ buildEnvironment }}</span>
            </div>

            <ng-container *ngIf="hasBuildInfo; else buildUnknown">
              <div class="admin-panel-version-line">
                <span class="admin-panel-version-commit" *ngIf="buildCommit; else buildCommitUnknown">{{ buildCommit }}</span>
                <ng-template #buildCommitUnknown>
                  <span class="admin-panel-version-commit is-unknown">commit unknown</span>
                </ng-template>
                <span class="admin-panel-version-branch" *ngIf="buildBranch">{{ buildBranch }}</span>
              </div>

              <div class="admin-panel-version-time" *ngIf="buildTimeLabel">
                <span>{{ buildTimeLabel }}</span>
                <span class="admin-panel-version-age" *ngIf="buildAge">{{ buildAge }}</span>
              </div>
            </ng-container>

            <ng-template #buildUnknown>
              <div class="admin-panel-version-line is-unknown">Build details unavailable</div>
            </ng-template>
          </div>

          <div class="admin-panel-version-actions">
            <button
              type="button"
              class="admin-panel-version-button"
              (click)="copyBuildDetails()"
              title="Copy build details"
              aria-label="Copy build details"
            >
              <fa-icon [icon]="faCopy"></fa-icon>
            </button>

            <button
              type="button"
              class="admin-panel-version-button"
              [disabled]="versionCheck() === 'checking'"
              (click)="checkForNewerBuild()"
              title="Check for a newer build"
              aria-label="Check for a newer build"
            >
              <fa-icon *ngIf="versionCheck() !== 'checking'; else versionChecking" [icon]="faArrowsRotate"></fa-icon>
              <ng-template #versionChecking>
                <fa-icon [icon]="faSpinnerThird" animation="spin"></fa-icon>
              </ng-template>
            </button>
          </div>
        </div>

        <div
          class="admin-panel-version-status"
          *ngIf="versionCheckMessage as versionMessage"
          [class.is-stale]="versionCheck() === 'stale'"
          [class.is-error]="versionCheck() === 'error'"
          role="status"
        >
          <span>{{ versionMessage }}</span>
          <button
            type="button"
            class="admin-panel-version-reload"
            *ngIf="versionCheck() === 'stale'"
            (click)="reloadForNewerBuild()"
          >
            Reload
          </button>
        </div>
      </div>
    </div>

    <div class="admin-tools-row">
      <button
        type="button"
        class="admin-toggle"
        [class.is-open]="adminActionMenuService.isOpen()"
        (click)="handleToggleClick($event)"
        aria-controls="admin-panel"
        [attr.aria-expanded]="adminActionMenuService.isOpen()"
        aria-label="Toggle admin tools"
      >
        <span class="admin-toggle-mark">
          <fa-icon [icon]="adminActionMenuService.isOpen() ? faXmark : faShieldHalved"></fa-icon>
        </span>
      </button>

      <button
        type="button"
        class="admin-notes-fab"
        *ngIf="adminNotesFabService.subject() as notesSubject"
        (click)="openNotesModal($event)"
        [attr.aria-label]="'Admin notes for ' + notesSubject.subjectName"
        [title]="'Admin notes for ' + notesSubject.subjectName"
      >
        <fa-icon [icon]="faFileLines"></fa-icon>
        <span class="admin-notes-fab-badge" *ngIf="adminNotesFabService.unresolvedCount() > 0">{{ notesBadgeLabel }}</span>
      </button>
    </div>
  </div>
</ng-container>
